/** Backend field: subscription_product_scope — 'ielts_only' | 'gre_only' | 'full' | null */

export const IELTS_JOURNEY_PATH = '/exams/english/journey';
export const DEFAULT_PREMIUM_HOME = '/home';

const ENGLISH_EXAM_PREFIX = '/exams/english';
const GRE_EXAM_PREFIX = '/exams/gre';
const TAGE_EXAM_PREFIX = '/exams/tage';

const MBA_APP_PREFIXES = [
  '/mbaapplication',
  '/applications',
  '/coach/reviews',
  '/coach/document-reviews',
  '/profilebuilder',
  '/cv',
  '/essays',
  '/interview',
  '/prepcopilot',
];

const GENERAL_GRADUATE_PREFIXES = [
  DEFAULT_PREMIUM_HOME,
  TAGE_EXAM_PREFIX,
  '/coaching/tage',
  ...MBA_APP_PREFIXES,
];

const FREE_OR_SHARED_PREFIXES = [
  '/pricing',
  '/calendar',
  '/onboarding',
  '/login',
  '/signup',
  '/forgotpassword',
  '/resetpassword',
  '/terms',
  '/payment-success',
  '/coaching/payment-success',
  '/coaching/booking-success',
  '/community',
  '/coach',
  '/resources',
  '/webinars',
];

const FREE_OR_SHARED_EXACT = new Set(['/settings', '/coaching']);

const GRE_SERVICE_TYPES = new Set([
  'gre_vocabulary',
  'gre_reading',
  'gre_quantitative',
]);

const ANCILLARY_SERVICE_TYPES = new Set([
  'ai_copilot',
  'premium_support',
  'unlimited_access',
]);

function pathMatchesPrefix(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function serviceTypesForUser(user) {
  const rows = Array.isArray(user?.active_premium_subscriptions)
    ? user.active_premium_subscriptions
    : [];
  const types = new Set();
  rows.forEach((sub) => {
    if (!Array.isArray(sub?.services)) return;
    sub.services.forEach((service) => {
      if (service?.type) types.add(String(service.type));
    });
  });
  return types;
}

function hasAnyType(types, candidates) {
  return candidates.some((candidate) => types.has(candidate));
}

function hasOnlyTypes(types, allowed) {
  if (types.size === 0) return false;
  return Array.from(types).every((type) => allowed.has(type));
}

export function isIeltsOnlySubscriber(user) {
  const types = serviceTypesForUser(user);
  if (types.size > 0) {
    return (
      types.has('ielts_preparation') &&
      !hasAnyType(types, ['mba_admission', ...GRE_SERVICE_TYPES]) &&
      hasOnlyTypes(
        types,
        new Set(['ielts_preparation', ...ANCILLARY_SERVICE_TYPES])
      )
    );
  }
  return user?.subscription_product_scope === 'ielts_only';
}

export function isGreOnlySubscriber(user) {
  const types = serviceTypesForUser(user);
  if (types.size > 0) {
    return (
      !types.has('ielts_preparation') &&
      hasAnyType(types, [...GRE_SERVICE_TYPES, 'mba_admission'])
    );
  }
  return user?.subscription_product_scope === 'gre_only';
}

export function canAccessEnglishPremiumRoutes(user) {
  if (!user?.is_premium) return false;
  const types = serviceTypesForUser(user);
  if (types.size > 0) return types.has('ielts_preparation');
  return !isGreOnlySubscriber(user);
}

export function canAccessGrePremiumRoutes(user) {
  if (!user?.is_premium) return false;
  const types = serviceTypesForUser(user);
  if (types.size > 0) return hasAnyType(types, [...GRE_SERVICE_TYPES]);
  return !isIeltsOnlySubscriber(user);
}

export function canAccessGraduateSuite(user) {
  if (!user?.is_premium) return false;
  const types = serviceTypesForUser(user);
  if (types.size > 0) {
    return hasAnyType(types, [...GRE_SERVICE_TYPES, 'mba_admission']);
  }
  return !isIeltsOnlySubscriber(user);
}

export function getPremiumLandingPath(user) {
  if (canAccessEnglishPremiumRoutes(user) && !canAccessGraduateSuite(user)) {
    return IELTS_JOURNEY_PATH;
  }
  if (isIeltsOnlySubscriber(user)) return IELTS_JOURNEY_PATH;
  return DEFAULT_PREMIUM_HOME;
}

function isSharedPath(pathname) {
  if (FREE_OR_SHARED_EXACT.has(pathname)) return true;
  return FREE_OR_SHARED_PREFIXES.some((p) => pathMatchesPrefix(pathname, p));
}

function isProtectedProductPath(pathname) {
  if (pathMatchesPrefix(pathname, ENGLISH_EXAM_PREFIX)) return true;
  if (pathMatchesPrefix(pathname, GRE_EXAM_PREFIX)) return true;
  return GENERAL_GRADUATE_PREFIXES.some((p) => pathMatchesPrefix(pathname, p));
}

function restrictedFallbackPath(user) {
  if (canAccessGraduateSuite(user)) return DEFAULT_PREMIUM_HOME;
  if (canAccessEnglishPremiumRoutes(user)) return IELTS_JOURNEY_PATH;
  return '/community';
}

export function subscriptionRouteRedirectTarget(pathname, user) {
  if (!isProtectedProductPath(pathname) || isSharedPath(pathname)) return null;

  if (!user) return '/login';
  if (user.role?.name === 'coach' || user.role?.name === 'admin') return null;
  if (!user.is_premium) return '/community';

  if (
    pathMatchesPrefix(pathname, ENGLISH_EXAM_PREFIX) &&
    !canAccessEnglishPremiumRoutes(user)
  ) {
    return restrictedFallbackPath(user);
  }

  if (
    pathMatchesPrefix(pathname, GRE_EXAM_PREFIX) &&
    !canAccessGrePremiumRoutes(user)
  ) {
    return restrictedFallbackPath(user);
  }

  if (
    GENERAL_GRADUATE_PREFIXES.some((p) => pathMatchesPrefix(pathname, p)) &&
    !canAccessGraduateSuite(user)
  ) {
    return restrictedFallbackPath(user);
  }

  return null;
}

/**
 * Backward-compatible helpers still used by some route logic.
 */
export function ieltsOnlyRedirectTarget(pathname, user) {
  if (!user?.is_premium) return null;
  if (user.role?.name === 'coach' || user.role?.name === 'admin') return null;
  if (!isIeltsOnlySubscriber(user)) return null;
  if (pathMatchesPrefix(pathname, ENGLISH_EXAM_PREFIX)) return null;
  if (isSharedPath(pathname)) return null;
  return IELTS_JOURNEY_PATH;
}

export function greOnlyEnglishRedirectTarget(pathname, user) {
  if (!user?.is_premium) return null;
  if (user.role?.name === 'coach' || user.role?.name === 'admin') return null;
  if (!isGreOnlySubscriber(user)) return null;
  if (!pathMatchesPrefix(pathname, ENGLISH_EXAM_PREFIX)) return null;
  return restrictedFallbackPath(user);
}
